import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { finalize, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PageLayoutComponent } from '../../components/page-layout/page-layout.component';
import { AwsWafCaptchaService } from '../../services/aws-waf-captcha.service';
import { EmailerService } from '../../services/emailer.service';

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    PageLayoutComponent,
  ],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly captcha = inject(AwsWafCaptchaService);
  private readonly emailer = inject(EmailerService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly subscriptions = new Subscription();

  readonly captchaContainerId = 'aws-waf-captcha-container';
  readonly usePatternedContactShell = true;
  readonly isLocalCaptchaMode = !environment.production;

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    message: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(1000)]],
  });

  captchaToken?: string;
  isMessageSendable = false;
  isCaptchaLoading = true;
  isSubmitting = false;
  captchaErrorMessage: string | null = null;

  ngOnInit(): void {
    if (this.isLocalCaptchaMode) {
      this.isCaptchaLoading = false;
      return;
    }

    this.subscriptions.add(
      this.captcha.getCaptchaLoadStatus().subscribe(isLoaded => {
        this.isCaptchaLoading = !isLoaded;
        this.cdr.markForCheck();
      }),
    );
  }

  ngAfterViewInit(): void {
    if (this.isLocalCaptchaMode) return;

    this.initializeCaptcha();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isFormSubmittable(): boolean {
    return this.form.valid && this.isMessageSendable && !!this.captchaToken && !this.isSubmitting;
  }

  submit(): void {
    if (!this.isFormSubmittable()) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, email, message } = this.form.getRawValue();
    const captchaToken = this.captchaToken;
    if (!captchaToken) return;

    this.isSubmitting = true;
    this.cdr.markForCheck();

    this.subscriptions.add(
      this.emailer
        .sendEmail(name.trim(), email.trim(), message.trim(), captchaToken)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: () => {
            this.snackBar.open(
              'Message sent. Please pass the captcha again to send another message.',
              'Close',
              { duration: 5000 },
            );
            this.form.reset();
            this.clearCaptchaState();
            if (!this.isLocalCaptchaMode) {
              this.captcha.resetCaptcha(this.captchaContainerId);
              this.initializeCaptcha();
            }
          },
          error: () => {
            this.snackBar.open(
              'An error occurred while sending your message. Please try again.',
              'Close',
              { duration: 5000 },
            );
          },
        }),
    );
  }

  passLocalCaptcha(): void {
    this.captchaToken = `debug-token-${Date.now()}`;
    this.isMessageSendable = true;
    this.isCaptchaLoading = false;
    this.captchaErrorMessage = null;
    this.cdr.markForCheck();
  }

  private initializeCaptcha(): void {
    this.isCaptchaLoading = true;
    this.captchaErrorMessage = null;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.captcha.initCaptcha({
        container: this.captchaContainerId,
        onSuccess: token => {
          this.captchaToken = token;
          this.isMessageSendable = true;
          this.isCaptchaLoading = false;
          this.captchaErrorMessage = null;
          this.cdr.detectChanges();
        },
        onError: error => {
          this.clearCaptchaState();
          this.isCaptchaLoading = false;
          this.captchaErrorMessage = this.userFacingCaptchaError(error);
          this.cdr.detectChanges();
        },
        onExpired: () => {
          this.clearCaptchaState();
          this.captchaErrorMessage = 'Captcha expired. Please try again.';
          this.cdr.detectChanges();
        },
      });
    }, 0);
  }

  private clearCaptchaState(): void {
    this.captchaToken = undefined;
    this.isMessageSendable = false;
  }

  private userFacingCaptchaError(error: Error): string {
    const message = error.message || '';
    if (
      /not configured/i.test(message) ||
      /did not load/i.test(message) ||
      /failed to load/i.test(message)
    ) {
      return 'The contact security check is temporarily unavailable. Please try again later.';
    }
    return 'Captcha failed. Please try again.';
  }
}
